import "reflect-metadata";
import { getInjectedData } from "./injector";
import { getServiceData } from "./service";
import { executeArrayMaybePromise, getAllPrototypes } from "./utils";
export class Container {
    constructor() {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        this.storage = new Map();
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        this.classes = new Map();
        // public constructor
    }
    get(Service) {
        const val = this.getMaybePromise(Service);
        if (val instanceof Promise) {
            throw new Error(`Tried to resolve async service with get(${Service.name}), please use load(${Service.name}) instead`);
        }
        return val;
    }
    register(Service, instance) {
        const serviceData = getServiceData(Service);
        if (!serviceData.singleton) {
            throw new Error(`Can only register singletons, not ${Service.name}`);
        }
        const all = getAllPrototypes(Service);
        for (const func of all) {
            if (this.storage.has(func)) {
                throw new Error(`When registering ${Service.name} found existing ${func.name} registered`);
            }
        }
        if (instance) {
            for (const func of all) {
                this.storage.set(func, instance);
            }
        }
        else {
            for (const func of all) {
                this.classes.set(func, Service);
            }
        }
    }
    load(Service) {
        return Promise.resolve(this.getMaybePromise(Service));
    }
    // This function is not intended to be used directly, use get(Service) instead
    getMaybePromise(Service) {
        const serviceData = getServiceData(Service);
        if (this.classes.has(Service)) {
            Service = this.classes.get(Service);
        }
        if (serviceData.singleton && this.storage.has(Service)) {
            return this.storage.get(Service);
        }
        const all = getAllPrototypes(Service);
        if (serviceData.singleton) {
            for (const func of all) {
                if (this.storage.has(func)) {
                    throw new Error(`When registering ${Service.name} found existing ${func.name} registered`);
                }
            }
        }
        const injectData = getInjectedData(Service.prototype);
        if (injectData.parameters.length !==
            Service.prototype.constructor.length) {
            throw new Error(`When registering ${Service.name} found ${injectData.parameters.length} parameters, but constructor has ${Service.prototype.constructor.length}`);
        }
        for (let i = 0; i < injectData.parameters.length; i++) {
            const provider = injectData.parameters[i];
            if (!provider) {
                throw new Error(`When registering ${Service.name}, parameter ${i} was found missing a @Inject()`);
            }
        }
        const params = executeArrayMaybePromise(injectData.parameters.map((param) => () => param(this, Service)));
        if (params instanceof Promise) {
            return params.then((values) => create(values));
        }
        const create = (params) => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const service = new Service(...params);
            if (serviceData.singleton) {
                for (const func of all) {
                    this.storage.set(func, service);
                }
            }
            const mapped = executeArrayMaybePromise(injectData.properties.map((provider) => () => provider(this, service)));
            const postInitialization = () => {
                let anyPromises = false;
                const mapped = injectData.postInjection.map((func) => {
                    const val = func.call(service, this);
                    anyPromises || (anyPromises = val instanceof Promise);
                    return val;
                });
                if (anyPromises) {
                    return Promise.all(mapped).then(() => service);
                }
                return service;
            };
            if (!(mapped instanceof Promise)) {
                return postInitialization();
            }
            return mapped.then(() => postInitialization());
        };
        return create(params);
    }
    getValue(key) {
        return this.storage.get(key);
    }
    setValue(key, value) {
        this.storage.set(key, value);
    }
    removeValue(key) {
        const val = this.storage.get(key);
        this.storage.delete(key);
        return val;
    }
}
export const globalContainer = new Container();
export function get(Service) {
    return globalContainer.get(Service);
}
export function load(Service) {
    return globalContainer.load(Service);
}
export function register(Service, instance) {
    globalContainer.register(Service, instance);
}
export function getValue(key) {
    return globalContainer.getValue(key);
}
export function setValue(key, value) {
    globalContainer.setValue(key, value);
}
export function removeValue(key) {
    return globalContainer.removeValue(key);
}
